import type { FormeJuridique } from '@shared/types/Tenant';
import { defaultParametresFiscauxPaie, parametresFromTenantSettings } from './ParametresFiscauxPaie';
import type { ParametresFiscauxPaie } from './ParametresFiscauxPaie';

/**
 * Contributions patronales activables par entreprise (décision du dirigeant du 23/10/2026) :
 *  - FNH 2 % (CGI 2025 art. 401 à 404) : employeurs affiliés à la CNSS, base = salaires dans la limite du plafond CNSS ;
 *  - CFP 0,50 % (CGI 2025, loi de finances 2017 art. 5 à 12) : sociétés soumises à l'IS, personnes physiques en BIC ou BNC.
 *
 * Deux conditions s'appliquent ENSEMBLE :
 *  1. la table de paramètres DATÉS (`ParametresPaieDates.ts`) active la contribution pour la période du bulletin
 *     (les bulletins générés avant ce choix ne sont jamais recalculés) ;
 *  2. l'interrupteur de l'entreprise est sur « oui ». Sans choix explicite, il vaut la valeur par défaut déduite du
 *     statut de l'entreprise.
 * Un interrupteur ne peut donc jamais activer une contribution que la table datée n'active pas encore.
 */

export type StatutFiscalEmployeur = 'societe_is' | 'bic_bnc' | 'autre';

export const STATUTS_FISCAUX_EMPLOYEUR: readonly StatutFiscalEmployeur[] = [
  'societe_is',
  'bic_bnc',
  'autre',
];

export const LIBELLES_STATUT_FISCAL_EMPLOYEUR: Record<StatutFiscalEmployeur, string> = {
  societe_is: 'Société soumise à l’impôt sur les sociétés (IS)',
  bic_bnc: 'Personne physique en BIC ou BNC',
  autre: 'Autre (association, administration, GIE…)',
};

/** Choix de l'entreprise, stockés dans `TenantSettings.contributions_employeur`. Absent = valeurs par défaut. */
export interface ContributionsEmployeurSettings {
  /** Statut fiscal de l'employeur. Absent = déduit de la forme juridique. */
  statut_fiscal?: StatutFiscalEmployeur;
  /** Choix explicite d'appliquer (true) ou non (false) la FNH. Absent = valeur par défaut. */
  fnh?: boolean;
  /** Choix explicite d'appliquer (true) ou non (false) la CFP. Absent = valeur par défaut. */
  cfp?: boolean;
}

export interface ContributionEntreprise {
  /** L'entreprise applique-t-elle la contribution (choix explicite, sinon valeur par défaut) ? */
  actif: boolean;
  /** Valeur par défaut déduite du statut. */
  parDefaut: boolean;
  /** `true` si l'entreprise a fait un choix qui diffère de la valeur par défaut. */
  choixExplicite: boolean;
}

export interface ContributionsEntreprise {
  statut: StatutFiscalEmployeur;
  /** `true` si le statut est celui choisi par l'entreprise, `false` s'il est déduit de la forme juridique. */
  statutChoisi: boolean;
  fnh: ContributionEntreprise;
  cfp: ContributionEntreprise;
}

/**
 * Statut déduit de la forme juridique quand l'entreprise n'a rien choisi.
 * SARL, SARLU, SA, SAS : sociétés de capitaux, soumises à l'IS. EI : personne physique en BIC/BNC.
 * Les autres formes (SNC, SCS, GIE, ESS, Autre) ne se déduisent pas avec certitude : « autre », à préciser par
 * l'entreprise (la CFP est alors désactivée par défaut).
 */
export function statutFiscalParDefaut(
  forme: FormeJuridique | string | null | undefined,
): StatutFiscalEmployeur {
  switch (forme) {
    case 'SARL':
    case 'SARLU':
    case 'SA':
    case 'SAS':
      return 'societe_is';
    case 'EI':
      return 'bic_bnc';
    default:
      return 'autre';
  }
}

/** FNH : due par tout employeur affilié à la CNSS (CGI art. 401 à 404). */
export function fnhParDefaut(_statut: StatutFiscalEmployeur): boolean {
  return true;
}

/** CFP : sociétés soumises à l'IS et personnes physiques en BIC ou BNC (LF 2017 art. 5 à 12). */
export function cfpParDefaut(statut: StatutFiscalEmployeur): boolean {
  return statut === 'societe_is' || statut === 'bic_bnc';
}

function estStatut(v: unknown): v is StatutFiscalEmployeur {
  return v === 'societe_is' || v === 'bic_bnc' || v === 'autre';
}

function lireSettings(raw: unknown): ContributionsEmployeurSettings {
  if (!raw || typeof raw !== 'object') return {};
  const r = raw as Record<string, unknown>;
  return {
    statut_fiscal: estStatut(r['statut_fiscal']) ? r['statut_fiscal'] : undefined,
    fnh: typeof r['fnh'] === 'boolean' ? r['fnh'] : undefined,
    cfp: typeof r['cfp'] === 'boolean' ? r['cfp'] : undefined,
  };
}

/** Résout statut et interrupteurs de l'entreprise (défauts compris). Ne lève jamais : une donnée inconnue vaut « défaut ». */
export function contributionsEntreprise(
  forme: FormeJuridique | string | null | undefined,
  tenantSettings: object | null | undefined,
): ContributionsEntreprise {
  const brut = (tenantSettings as Record<string, unknown> | null | undefined)?.['contributions_employeur'];
  const s = lireSettings(brut);
  const statut = s.statut_fiscal ?? statutFiscalParDefaut(forme);
  const fnhDef = fnhParDefaut(statut);
  const cfpDef = cfpParDefaut(statut);
  const fnh = s.fnh ?? fnhDef;
  const cfp = s.cfp ?? cfpDef;
  return {
    statut,
    statutChoisi: s.statut_fiscal !== undefined,
    fnh: { actif: fnh, parDefaut: fnhDef, choixExplicite: s.fnh !== undefined && s.fnh !== fnhDef },
    cfp: { actif: cfp, parDefaut: cfpDef, choixExplicite: s.cfp !== undefined && s.cfp !== cfpDef },
  };
}

/**
 * Applique les interrupteurs de l'entreprise aux paramètres de la période : une contribution désactivée pour
 * l'entreprise vaut 0 (taux effectif et état). Une contribution déjà inactive pour la période le reste.
 */
export function appliquerContributionsEntreprise(
  params: ParametresFiscauxPaie,
  c: ContributionsEntreprise,
): ParametresFiscauxPaie {
  const ce = structuredClone(params.contributionsEnAttente);
  let tauxCds = params.tauxCds;
  let tauxTfp = params.tauxTfp;
  if (!c.fnh.actif) {
    tauxCds = 0;
    ce.fnh = { ...ce.fnh, actif: false };
  }
  if (!c.cfp.actif) {
    tauxTfp = 0;
    ce.cfp = { ...ce.cfp, actif: false };
  }
  return { ...params, tauxCds, tauxTfp, contributionsEnAttente: ce };
}

/**
 * Paramètres de paie applicables à UNE entreprise pour UNE période : table datée de la période, surcharge fiscale
 * éventuelle (`paie_fiscale`), puis interrupteurs FNH / CFP de l'entreprise.
 * C'est le point d'entrée à utiliser partout où un bulletin est calculé.
 */
export function parametresPaieEntreprise(
  tenant: {
    forme_juridique?: FormeJuridique | string | null;
    parametres?: Record<string, unknown> | object | null;
  } | null | undefined,
  periode?: string,
): ParametresFiscauxPaie {
  const settings = (tenant?.parametres ?? undefined) as Record<string, unknown> | undefined;
  const base = settings
    ? parametresFromTenantSettings(settings, periode)
    : defaultParametresFiscauxPaie(periode);
  return appliquerContributionsEntreprise(
    base,
    contributionsEntreprise(tenant?.forme_juridique, settings),
  );
}
